/**
 * WeeklyTrendsView component for displaying time-series metrics.
 */

import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { LineChart } from '@/components/shared/Charts'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TrendingUp, TrendingDown, Users, Bot, Coins, DollarSign } from 'lucide-react'
import { useWeeklyTrends } from '../hooks/use-stats'
import type { Scope } from '../types'

interface WeeklyTrendsViewProps {
  scope: Scope
  tenantId: string | null
  organizationId: string | null
}

export function WeeklyTrendsView({
  scope,
  tenantId,
  organizationId
}: WeeklyTrendsViewProps) {
  const [weeks, setWeeks] = useState(8)
  const [domainFilter, setDomainFilter] = useState<string>('')

  // Check if required parameters are present based on scope
  const hasRequiredParams =
    scope === 'system' ? true :
    scope === 'tenant' ? !!tenantId :
    scope === 'organization' ? !!(tenantId && organizationId) :
    false

  const { data, isLoading, error } = useWeeklyTrends({
    weeks,
    scope,
    tenantId,
    organizationId,
    domain: domainFilter || undefined
  })

  // Show message if required parameters are missing
  if (!hasRequiredParams) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-12 text-muted-foreground">
            {scope === 'tenant' && !tenantId && 'Please select a tenant to view weekly trends'}
            {scope === 'organization' && !tenantId && 'Please select a tenant first'}
            {scope === 'organization' && tenantId && !organizationId && 'Please select an organization to view weekly trends'}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-destructive">
            Error loading weekly trends: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader>
                <div className="h-4 bg-muted animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted animate-pulse rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  const summary = data?.summary
  const weekData = data?.weeks || []

  // Prepare chart data
  const chartData = weekData.map(week => ({
    weekLabel: week.weekLabel,
    users: week.metrics.newUsers,
    agents: week.metrics.agentsCreated,
    tokens: week.metrics.totalTokens,
    cost: week.metrics.totalCost
  }))

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <Label htmlFor="weeks">Time Period</Label>
              <Select value={weeks.toString()} onValueChange={(val) => setWeeks(parseInt(val))}>
                <SelectTrigger id="weeks">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="4">Last 4 Weeks</SelectItem>
                  <SelectItem value="8">Last 8 Weeks</SelectItem>
                  <SelectItem value="12">Last 12 Weeks</SelectItem>
                  <SelectItem value="26">Last 26 Weeks</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <Label htmlFor="domain">Filter by Domain (Optional)</Label>
              <Input
                id="domain"
                placeholder="e.g., example.com"
                value={domainFilter}
                onChange={(e) => setDomainFilter(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">New Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.totalUsers.toLocaleString() || 0}</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              {summary?.growthRate.users && summary.growthRate.users > 0 ? (
                <><TrendingUp className="h-3 w-3 mr-1 text-green-600" />
                <span className="text-green-600">+{summary.growthRate.users}%</span></>
              ) : summary?.growthRate.users && summary.growthRate.users < 0 ? (
                <><TrendingDown className="h-3 w-3 mr-1 text-red-600" />
                <span className="text-red-600">{summary.growthRate.users}%</span></>
              ) : (
                <span>No change</span>
              )}
              <span className="ml-1">vs avg</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Agents Created</CardTitle>
            <Bot className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.totalAgents.toLocaleString() || 0}</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              {summary?.growthRate.agents && summary.growthRate.agents > 0 ? (
                <><TrendingUp className="h-3 w-3 mr-1 text-green-600" />
                <span className="text-green-600">+{summary.growthRate.agents}%</span></>
              ) : summary?.growthRate.agents && summary.growthRate.agents < 0 ? (
                <><TrendingDown className="h-3 w-3 mr-1 text-red-600" />
                <span className="text-red-600">{summary.growthRate.agents}%</span></>
              ) : (
                <span>No change</span>
              )}
              <span className="ml-1">vs avg</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tokens</CardTitle>
            <Coins className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.totalTokens.toLocaleString() || 0}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Avg: {summary?.averageUsersPerWeek.toFixed(0)} users/week
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${summary?.totalCost.toFixed(2) || '0.00'}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Avg: {summary?.averageAgentsPerWeek.toFixed(0)} agents/week
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6">
        {/* User & Agent Trends */}
        <LineChart
          title="User & Agent Growth Trends"
          data={chartData}
          dataKeys={[
            { key: 'users', label: 'New Users', color: '#3b82f6' },
            { key: 'agents', label: 'Agents Created', color: '#10b981' }
          ]}
          xAxisKey="weekLabel"
          height={350}
          loading={isLoading}
        />

        {/* Token & Cost Trends */}
        <LineChart
          title="Token Usage & Cost Trends"
          data={chartData}
          dataKeys={[
            { key: 'tokens', label: 'Tokens', color: '#8b5cf6' },
            { key: 'cost', label: 'Cost ($)', color: '#f59e0b' }
          ]}
          xAxisKey="weekLabel"
          height={350}
          loading={isLoading}
        />
      </div>

      {/* Weekly Breakdown Table */}
      <Card>
        <CardHeader>
          <CardTitle>Weekly Breakdown</CardTitle>
          <CardDescription>
            Detailed metrics for each week
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Week</th>
                  <th className="text-left p-2">Period</th>
                  <th className="text-right p-2">New Users</th>
                  <th className="text-right p-2">Agents</th>
                  <th className="text-right p-2">Tokens</th>
                  <th className="text-right p-2">Cost</th>
                </tr>
              </thead>
              <tbody>
                {weekData.map((week) => (
                  <tr key={`${week.year}-${week.weekNumber}`} className="border-b hover:bg-muted/50">
                    <td className="p-2 font-medium">{week.weekLabel}</td>
                    <td className="p-2 text-sm text-muted-foreground">
                      {week.weekStart} to {week.weekEnd}
                    </td>
                    <td className="text-right p-2">{week.metrics.newUsers.toLocaleString()}</td>
                    <td className="text-right p-2">{week.metrics.agentsCreated.toLocaleString()}</td>
                    <td className="text-right p-2">{week.metrics.totalTokens.toLocaleString()}</td>
                    <td className="text-right p-2">${week.metrics.totalCost.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {weekData.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No data available for the selected time period
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
